import { AdvancedColorPicker } from '@/components/advanced-color-picker'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { getRandomPastelColor } from '@/lib/color-utils'
import { listDepartmentsWithRelationsQuery } from '@/queries/department.query'
import { DepartmentService } from '@/services/department.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconArrowsRandom } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Department } from '../schema'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentRow?: Department | null
}

const formSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1, 'Nome é obrigatório.'),
  color: z.string().min(1, 'Cor é obrigatória.'),
});

type DepartmentsForm = z.infer<typeof formSchema>

export function DepartmentsMutateDrawer({ open, onOpenChange, currentRow }: Props) {
  const isUpdate = !!currentRow

  const form = useForm<DepartmentsForm>({
    resolver: zodResolver(formSchema),
    defaultValues: currentRow ?? {
      name: '',
      color: getRandomPastelColor(),
    },
  })

  const queryClient = useQueryClient()

  const onSubmit = (data: DepartmentsForm) => {
    const mutation = isUpdate ? DepartmentService.update(currentRow.id, data) : DepartmentService.create(data)
    toast.promise(mutation, {
      loading: 'Salvando departamento...',
      success: () => {
        queryClient.invalidateQueries(listDepartmentsWithRelationsQuery)
        onOpenChange(false)
        form.reset()
        return 'Departamento salvo'
      },
      error: () => {
        queryClient.invalidateQueries(listDepartmentsWithRelationsQuery)
        return 'Erro ao salvar departamento.'
      },
    })
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        form.reset()
      }}
    >
      <SheetContent className='flex flex-col'>
        <SheetHeader className='text-left'>
          <SheetTitle>{isUpdate ? 'Atualizar' : 'Adicionar'} Departamento</SheetTitle>
          <SheetDescription>
            {isUpdate
              ? 'Atualize o departamento fornecendo as informações necessárias.'
              : 'Adicione um novo departamento fornecendo as informações necessárias.'}
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            id='tasks-form'
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex-1 space-y-5'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Nome do segmento' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='color'
              render={({ field }) => {
                return (
                  <FormItem className='space-y-1 flex flex-col'>
                    <FormLabel>Cor</FormLabel>
                    <FormControl>
                      <div className='flex items-center gap-2'>
                        <AdvancedColorPicker
                          color={field.value}
                          onChange={field.onChange}
                        />
                        <Button
                          variant='outline'
                          type='button'
                          size='icon'
                          className='p-0'
                          onClick={() => {
                            field.onChange(getRandomPastelColor())
                          }}
                        >
                          <IconArrowsRandom className='w-4 h-4' />
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )
              }}
            />
          </form>
        </Form>
        <SheetFooter className='gap-2'>
          <SheetClose asChild>
            <Button variant='outline'>Cancelar</Button>
          </SheetClose>
          <Button form='tasks-form' type='submit'>
            {isUpdate ? 'Atualizar' : 'Adicionar'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
